import React, { useState, useEffect, useRef, useCallback } from 'react';
import { adminApi } from '../../services/adminApi';
import type { LogEntryDto, LoggerInfoDto, SetLogLevelRequest } from '../../services/adminApi.types';
import Toast from '../../components/Toast';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import './LogsTab.css';

// Constants
const LOG_LEVELS: SetLogLevelRequest['level'][] = ['TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR', 'OFF'];
const DEFAULT_LOG_LIMIT = 1000;
const AUTO_REFRESH_INTERVAL_MS = 5000; // 5 seconds
const MAX_LOGGER_SUGGESTIONS = 20;
const MAX_CONSECUTIVE_ERRORS = 3; // Disable auto-refresh after 3 consecutive errors
const ERROR_BACKOFF_MS = 10000; // Wait 10 seconds before retrying after errors

// Allowed logger patterns (must match backend validation in LogManagementController)
// Backend now allows any valid Java logger name following naming conventions:
// - ROOT logger
// - Any logger starting with a letter, followed by alphanumeric, dots, underscores, dollar signs, or hyphens
// Examples: io.micrometer.registry.otlp.OtlpMeterRegistry, directory.brick.app.MyClass
const VALIDATION_MESSAGE = 'Logger name must follow Java naming conventions: start with a letter, followed by alphanumeric characters, dots, underscores, dollar signs, or hyphens. Example: io.micrometer.registry.otlp.OtlpMeterRegistry';

/**
 * Helper function to check if logger should be displayed in the table.
 *
 * This filter reduces UI noise by only showing relevant loggers by default:
 * - All loggers with explicitly configured levels (user-configured loggers)
 * - Common framework and application loggers that are frequently monitored
 *
 * Note: Custom loggers configured via the form (e.g., io.micrometer.*, reactor.*)
 * will automatically appear after configuration because they have configuredLevel !== null.
 */
const shouldDisplayLogger = (logger: LoggerInfoDto): boolean => {
    // Show any explicitly configured logger (includes custom loggers added via form)
    if (logger.configuredLevel !== null) {
        return true;
    }

    // Show commonly monitored framework and application loggers
    // These help users find frequently used loggers without overwhelming the UI
    return logger.name === 'ROOT' ||
           logger.name.includes('directory.brick') ||
           logger.name.includes('org.springframework') ||
           logger.name.includes('org.hibernate') ||
           logger.name.includes('com.zaxxer.hikari') ||
           logger.name.includes('io.micrometer') ||
           logger.name.includes('reactor.netty') ||
           logger.name.includes('com.netflix');
};

export const LogsTab: React.FC = () => {
    const [logs, setLogs] = useState<LogEntryDto[]>([]);
    const [loggers, setLoggers] = useState<LoggerInfoDto[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [stats, setStats] = useState<{ currentSize: number; maxSize: number } | null>(null);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
    const [showClearConfirm, setShowClearConfirm] = useState(false);
    const [consecutiveErrors, setConsecutiveErrors] = useState(0);

    // Custom logger form
    const [customLoggerName, setCustomLoggerName] = useState('');
    const [customLoggerLevel, setCustomLoggerLevel] = useState<SetLogLevelRequest['level']>('INFO');
    const [customLoggerError, setCustomLoggerError] = useState<string | null>(null);

    // Filters
    const [levelFilter, setLevelFilter] = useState<string>('');
    const [searchFilter, setSearchFilter] = useState('');
    const [loggerFilter, setLoggerFilter] = useState('');
    const [limit, setLimit] = useState(DEFAULT_LOG_LIMIT);

    const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const logsEndRef = useRef<HTMLDivElement>(null);

    // Store latest versions of functions in refs to avoid interval recreation
    const loadLogsRef = useRef<(() => Promise<void>) | undefined>(undefined);
    const loadStatsRef = useRef<(() => Promise<void>) | undefined>(undefined);

    const loadLogs = useCallback(async () => {
        try {
            const data = await adminApi.getLogs(
                levelFilter || undefined,
                searchFilter || undefined,
                loggerFilter || undefined,
                limit
            );
            setLogs(data);
            setError(null);
            setConsecutiveErrors(0); // Reset error count on success
        } catch (err) {
            const newErrorCount = consecutiveErrors + 1;
            setConsecutiveErrors(newErrorCount);
            setError(`Failed to load logs (${newErrorCount}/${MAX_CONSECUTIVE_ERRORS} errors)`);
            console.error(err);

            // Disable auto-refresh after max consecutive errors
            if (newErrorCount >= MAX_CONSECUTIVE_ERRORS && autoRefresh) {
                setAutoRefresh(false);
                setToast({
                    message: 'Auto-refresh disabled due to repeated errors. Click Refresh to retry.',
                    type: 'error'
                });
            }
        }
    }, [levelFilter, searchFilter, loggerFilter, limit, consecutiveErrors, autoRefresh]);

    const loadLoggers = useCallback(async () => {
        try {
            const data = await adminApi.getLoggers();
            setLoggers(data);
        } catch (err) {
            console.error('Failed to load loggers:', err);
        }
    }, []);

    const loadStats = useCallback(async () => {
        try {
            const data = await adminApi.getLogStats();
            if (data.available) {
                setStats({
                    currentSize: data.currentSize!,
                    maxSize: data.maxSize!
                });
            }
        } catch (err) {
            // Stats failures are less critical, just log them
            console.error('Failed to load stats:', err);
        }
    }, []);

    // Update refs whenever callbacks change
    useEffect(() => {
        loadLogsRef.current = loadLogs;
        loadStatsRef.current = loadStats;
    }, [loadLogs, loadStats]);

    // Initial load
    useEffect(() => {
        loadLogs();
        loadLoggers();
        loadStats();
    }, [loadLogs, loadLoggers, loadStats]);

    // Auto-refresh interval with exponential backoff on errors
    useEffect(() => {
        if (autoRefresh) {
            // Calculate interval based on consecutive errors (exponential backoff)
            const interval = consecutiveErrors > 0
                ? AUTO_REFRESH_INTERVAL_MS + (ERROR_BACKOFF_MS * Math.min(consecutiveErrors, 3))
                : AUTO_REFRESH_INTERVAL_MS;

            refreshIntervalRef.current = setInterval(() => {
                loadLogsRef.current?.();
                loadStatsRef.current?.();
            }, interval);
        } else {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
                refreshIntervalRef.current = null;
            }
        }

        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, [autoRefresh, consecutiveErrors]); // Recreate interval when error count changes

    const handleSetLogLevel = async (loggerName: string, level: SetLogLevelRequest['level']) => {
        try {
            const updatedLogger = await adminApi.setLoggerLevel(loggerName, level);
            // Optimistic UI update
            setLoggers(prevLoggers =>
                prevLoggers.map(logger =>
                    logger.name === loggerName ? updatedLogger : logger
                )
            );
            setToast({ message: 'Log level updated successfully', type: 'success' });
            // Background refresh for consistency
            loadLoggers().catch(err => console.error('Background refresh failed:', err));
        } catch (err) {
            console.error('Failed to set log level:', err);
            setToast({
                message: err instanceof Error ? err.message : 'Failed to set log level',
                type: 'error'
            });
        }
    };

    const handleSetCustomLoggerLevel = async (e: React.FormEvent) => {
        e.preventDefault();
        setCustomLoggerError(null); // Clear previous errors

        if (!customLoggerName.trim()) {
            const errorMsg = 'Please enter a logger name';
            setCustomLoggerError(errorMsg);
            setToast({ message: errorMsg, type: 'error' });
            return;
        }

        try {
            const updatedLogger = await adminApi.setLoggerLevel(customLoggerName.trim(), customLoggerLevel);

            // Update the loggers list with the new/updated logger
            setLoggers(prevLoggers => {
                const existingIndex = prevLoggers.findIndex(l => l.name === updatedLogger.name);
                if (existingIndex >= 0) {
                    // Update existing logger
                    return prevLoggers.map((logger, idx) =>
                        idx === existingIndex ? updatedLogger : logger
                    );
                } else {
                    // Add new logger to the list
                    return [...prevLoggers, updatedLogger].sort((a, b) => a.name.localeCompare(b.name));
                }
            });

            setToast({ message: `Log level for ${customLoggerName} set to ${customLoggerLevel}`, type: 'success' });
            setCustomLoggerName(''); // Clear the input
            setCustomLoggerError(null); // Clear error state

            // Refresh in background for consistency
            loadLoggers().catch(err => console.error('Background refresh failed:', err));
        } catch (err) {
            console.error('Failed to set custom logger level:', err);
            const errorMessage = err instanceof Error ? err.message : 'Failed to set log level';
            const displayMessage = errorMessage.includes('400')
                ? VALIDATION_MESSAGE
                : errorMessage;
            setCustomLoggerError(displayMessage);
            setToast({
                message: displayMessage,
                type: 'error'
            });
        }
    };

    const handleClearLogs = async () => {
        setShowClearConfirm(false);
        try {
            const result = await adminApi.clearLogs();
            setToast({ message: result.message, type: 'success' });
            await loadLogs();
            await loadStats();
        } catch (err) {
            console.error('Failed to clear logs:', err);
            setToast({
                message: err instanceof Error ? err.message : 'Failed to clear logs',
                type: 'error'
            });
        }
    };

    const scrollToBottom = () => {
        logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const scrollToTop = () => {
        const container = document.querySelector('.logs-container');
        container?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCopyLogs = async () => {
        try {
            // Format logs as plain text
            const logsText = logs.map(log => {
                const timestamp = formatTimestamp(log.timestamp);
                return `${timestamp} ${log.level.padEnd(5)} [${log.threadName}] ${log.logger} - ${log.message}`;
            }).join('\n');

            await navigator.clipboard.writeText(logsText);
            setToast({ message: `Copied ${logs.length} log entries to clipboard`, type: 'success' });
        } catch (err) {
            console.error('Failed to copy logs:', err);
            setToast({
                message: err instanceof Error ? err.message : 'Failed to copy logs to clipboard',
                type: 'error'
            });
        }
    };

    const handleLoggerClick = (loggerName: string) => {
        setCustomLoggerName(loggerName);
        // Scroll to the custom logger form
        const form = document.querySelector('.custom-logger-form');
        form?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const formatTimestamp = (timestamp: number): string => {
        const date = new Date(timestamp);
        return date.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
    };

    const getLevelColor = (level: string): string => {
        switch (level) {
            case 'ERROR': return '#ef4444';
            case 'WARN': return '#f59e0b';
            case 'INFO': return '#3b82f6';
            case 'DEBUG': return '#6b7280';
            case 'TRACE': return '#9ca3af';
            default: return '#6b7280';
        }
    };

    // Get unique logger names from current logs for filter dropdown
    const uniqueLoggers = Array.from(new Set(logs.map(log => log.logger)))
        .sort()
        .slice(0, MAX_LOGGER_SUGGESTIONS);

    return (
        <div className="logs-tab">
            <div className="logs-header">
                <h2>Application Logs</h2>
                {stats && (
                    <div className="logs-stats">
                        {stats.currentSize} / {stats.maxSize} entries ({Math.round(stats.currentSize / stats.maxSize * 100)}%)
                    </div>
                )}
            </div>

            {/* Log Viewer Section */}
            <div className="logs-viewer-section">
                <div className="logs-controls">
                    <div className="filter-group">
                        <label>Level:</label>
                        <input
                            type="text"
                            list="level-options"
                            value={levelFilter}
                            onChange={(e) => setLevelFilter(e.target.value)}
                            placeholder="All Levels"
                        />
                        <datalist id="level-options">
                            <option value="">All Levels</option>
                            {LOG_LEVELS.map(level => (
                                <option key={level} value={level}>{level}</option>
                            ))}
                        </datalist>
                    </div>

                    <div className="filter-group">
                        <label>Search:</label>
                        <input
                            type="text"
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            placeholder="Search message or logger..."
                        />
                    </div>

                    <div className="filter-group">
                        <label>Logger:</label>
                        <input
                            type="text"
                            list="logger-options"
                            value={loggerFilter}
                            onChange={(e) => setLoggerFilter(e.target.value)}
                            placeholder="All Loggers"
                        />
                        <datalist id="logger-options">
                            <option value="">All Loggers</option>
                            {uniqueLoggers.map(logger => (
                                <option key={logger} value={logger}>{logger}</option>
                            ))}
                        </datalist>
                    </div>

                    <div className="filter-group">
                        <label>Limit:</label>
                        <input
                            type="text"
                            list="limit-options"
                            value={limit}
                            onChange={(e) => setLimit(Number(e.target.value) || DEFAULT_LOG_LIMIT)}
                            placeholder={String(DEFAULT_LOG_LIMIT)}
                        />
                        <datalist id="limit-options">
                            <option value="100">100</option>
                            <option value="500">500</option>
                            <option value="1000">1000</option>
                            <option value="2000">2000</option>
                            <option value="5000">5000</option>
                            <option value="10000">10000</option>
                        </datalist>
                    </div>

                    <div className="action-buttons">
                        <button
                            className={`auto-refresh-btn ${autoRefresh ? 'active' : ''}`}
                            onClick={() => setAutoRefresh(!autoRefresh)}
                        >
                            {autoRefresh ? '✓' : '○'} Auto-refresh
                        </button>
                        <button onClick={loadLogs}>Refresh</button>
                        <button onClick={handleCopyLogs}>📋 Copy</button>
                        <button onClick={scrollToTop}>⬆ Top</button>
                        <button onClick={scrollToBottom}>⬇ Bottom</button>
                        <button onClick={() => setShowClearConfirm(true)} className="danger-btn">Clear Logs</button>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="logs-container">
                    {logs.length === 0 ? (
                        <div className="no-logs">No logs available</div>
                    ) : (
                        logs.map((log, index) => (
                            <div key={index} className="log-entry">
                                <div className="log-metadata">
                                    <span className="log-timestamp">{formatTimestamp(log.timestamp)}</span>
                                    <span className="log-level" style={{ color: getLevelColor(log.level) }}>
                                        {log.level.padEnd(5)}
                                    </span>
                                    <span className="log-thread">[{log.threadName}]</span>
                                    <span
                                        className="log-logger clickable"
                                        onClick={() => handleLoggerClick(log.logger)}
                                        title="Click to configure this logger"
                                    >
                                        {log.logger}
                                    </span>
                                </div>
                                <div className="log-message">{log.message}</div>
                            </div>
                        ))
                    )}
                    <div ref={logsEndRef} />
                </div>
            </div>

            {/* Logger Management Section */}
            <div className="logger-management-section">
                <h3>Logger Configuration</h3>

                {/* Custom Logger Form */}
                <form onSubmit={handleSetCustomLoggerLevel} className="custom-logger-form">
                    <div className="form-group">
                        <label htmlFor="customLoggerName">Logger Name:</label>
                        <input
                            id="customLoggerName"
                            type="text"
                            value={customLoggerName}
                            onChange={(e) => {
                                setCustomLoggerName(e.target.value);
                                if (customLoggerError) setCustomLoggerError(null);
                            }}
                            placeholder="e.g., directory.brick.app.MyClass"
                            aria-required="true"
                            aria-invalid={customLoggerError ? 'true' : 'false'}
                            aria-describedby={customLoggerError ? 'customLoggerError' : undefined}
                        />
                        {customLoggerError && (
                            <span id="customLoggerError" className="error-text" role="alert">
                                {customLoggerError}
                            </span>
                        )}
                    </div>
                    <div className="form-group">
                        <label htmlFor="customLoggerLevel">Level:</label>
                        <select
                            id="customLoggerLevel"
                            value={customLoggerLevel}
                            onChange={(e) => setCustomLoggerLevel(e.target.value as SetLogLevelRequest['level'])}
                            aria-required="true"
                        >
                            {LOG_LEVELS.map(level => (
                                <option key={level} value={level}>{level}</option>
                            ))}
                        </select>
                    </div>
                    <button type="submit" className="set-level-btn">Set Level</button>
                </form>

                <div className="loggers-table-container">
                    <table className="loggers-table">
                        <thead>
                            <tr>
                                <th>Logger Name</th>
                                <th>Effective Level</th>
                                <th>Configured Level</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loggers.filter(shouldDisplayLogger).map(logger => (
                                <tr key={logger.name}>
                                    <td className="logger-name">{logger.name}</td>
                                    <td>{logger.effectiveLevel}</td>
                                    <td>{logger.configuredLevel || <em>inherited</em>}</td>
                                    <td>
                                        <select
                                            value={logger.configuredLevel || logger.effectiveLevel}
                                            onChange={(e) => handleSetLogLevel(logger.name, e.target.value as SetLogLevelRequest['level'])}
                                        >
                                            {LOG_LEVELS.map(level => (
                                                <option key={level} value={level}>{level}</option>
                                            ))}
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            <ConfirmDialog
                isOpen={showClearConfirm}
                title="Clear All Logs"
                message="Are you sure you want to clear all logs from the in-memory buffer? This action cannot be undone."
                confirmLabel="Clear Logs"
                cancelLabel="Cancel"
                onConfirm={handleClearLogs}
                onCancel={() => setShowClearConfirm(false)}
            />
        </div>
    );
};
